import { reactive, ref, readonly, watch, onBeforeMount } from 'vue'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'
import { store } from '@remote/store'
import { checkNetworkState } from '@remote/store/utils'
import {
  getAlbumFrom,
  deleteAlbumFrom,
  addAlbumFrom,
  TGetAlbumFromPathItem,
  updateAlbumFromAll,
} from '@remote/api/adapter/xos-mqtt'
import { logInfo, logError } from '@remote/utils/logger'
import { getErrorMessage } from '@remote/utils/base'
import { toast } from '@remote/utils'
import type { IHttpScrapePathStruct } from '@remote/type/cinema'
import type { TEmptyType } from '@remote/views/scrape-settings/type'

function logs (..._args: unknown[]) {
  console.log('[useScrapeSettings]', ..._args)
}

export interface IStoreState {
  /** 是否开启相册备份 */
  isScrapeOpened: boolean
  /** 是否全盘刮削 */
  isScrapeAll: boolean
  /** 刮削目录列表 */
  scrapePaths: IHttpScrapePathStruct[]
}

interface IStoreErrorState {
  type: TEmptyType | ''
  // hints?: string | string[]
  hints?: string
}

export const useAlbumSourceSettingsStore = defineStore('album_source_settings', () => {
  const traceRef = readonly(ref<string>(uuidv4()))
  const state: IStoreState = reactive({
    isScrapeOpened: false,
    isScrapeAll: false,
    scrapePaths: [],
  })
  const error: IStoreErrorState = reactive({ type: '', hints: '' })
  function setError (type: IStoreErrorState['type'], msg: IStoreErrorState['hints'] = '') {
    error.type = type
    error.hints = msg
  }
  const isIniting = ref(true)
  const isLoading = ref(false)
  const lockRef = reactive({ isScrapeAll: false })
  function reset () {
    state.isScrapeOpened = false
    state.isScrapeAll = false
    state.scrapePaths = []
    isIniting.value = true
    isLoading.value = false
  }

  async function refresh () {
    if (isLoading.value) return
    // const isDev = process.env.NODE_ENV === 'development'
    if (checkNetworkState(true)) {
      setError('networkOffline', '尝试点击屏幕重新加载')
      throw new Error('网络异常，暂不支持操作')
    }
    const traceId = traceRef.value
    isLoading.value = true
    logs('[刮削设置][request]:', traceId)
    return getAlbumFrom({ platform: 'pc', limit: 200, page_token: '' }).then((result) => {
      logs('[刮削设置][response]:', traceId, result)
      setError('')
      const { list, isScrape, isScrapeAll } = result
      state.isScrapeOpened = isScrape
      state.isScrapeAll = isScrapeAll
      state.scrapePaths = list
      return result
    }).catch(err => {
      console.error(err)
      const msg = getErrorMessage(err)
      if (msg === 'timeout') {
        setError('timeout', '请稍后重试')
      } else {
        setError('someError', msg)
      }
      throw err
    }).finally(() => {
      isLoading.value = false
    })
  }

  async function init () {
    reset()
    refresh().finally(() => {
      isIniting.value = false
    })
  }

  async function toggleScrapeAll (checked = !state.isScrapeAll) {
    if (lockRef.isScrapeAll) return
    const traceId = traceRef.value
    try {
      lockRef.isScrapeAll = true
      state.isScrapeAll = checked
      await updateAlbumFromAll(checked)
      return true
    } catch (err) {
      console.error(err)
      const msg = getErrorMessage(err)
      toast(msg)
      state.isScrapeAll = !checked
      logError('cinema', {
        traceId,
        category: 'scrape_path',
        action: 'toggle_all_scrape',
        desc: (checked ? '开启' : '关闭') + '全盘刮削出现异常',
        params: { isAllScrape: checked },
        error: err,
        errorText: msg,
      })
      return false
    } finally {
      lockRef.isScrapeAll = false
    }
  }

  async function deletePath (pid: string) {
    if (checkNetworkState(false)) {
      return false
    }
    const traceId = traceRef.value
    const currentList = state.scrapePaths
    const delItem = currentList.find(item => item.path_id === pid)
    logs('删除项目:', delItem)
    if (!delItem) throw new Error('目标不存在')
    logInfo('cinema', {
      traceId,
      category: 'scrape_path',
      action: 'del_scrape_path',
      desc: '移除刮削目录',
      params: { item: delItem },
    })
    return deleteAlbumFrom([pid]).then(() => {
      const list = currentList.filter(data => data.path_id !== pid)
      state.scrapePaths = list
      logInfo('cinema', {
        traceId,
        category: 'scrape_path',
        action: 'del_scrape_path_succ',
        desc: '移除刮削目录成功',
        params: { item: delItem },
      })

      toast('删除成功')
      return true
    }).catch(err => {
      console.error(err)
      const msg = getErrorMessage(err)
      const toastMsg = msg === 'timeout' ? '网络开小差了，请重试' : (msg || '删除失败')
      toast(toastMsg)
      logError('cinema', {
        traceId,
        category: 'scrape_path',
        action: 'del_scrape_path_fail',
        desc: '移除刮削目录出现异常',
        params: { item: delItem },
        error: err,
        errorText: msg,
      })
      return false
    })
  }

  async function addPath (pathList: Array<Omit<TGetAlbumFromPathItem, 'path_id'>>) {
    const currentList = state.scrapePaths
    const traceId = traceRef.value
    const paths = pathList.filter(p => !!p.path)
    // 添加空目录，忽略本次添加
    if (!paths.length) return true
    try {
      logInfo('cinema', {
        traceId,
        category: 'scrape_path',
        action: 'add_scrape_path',
        desc: '添加刮削目录',
        params: { paths },
      })
      const result = await addAlbumFrom({ paths })
      if (result.ret === 0) {
        logs('已添加刮削路径:', paths)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const list = paths.concat(currentList.filter(p => !paths.some(np => np.path === p.path)))
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        state.scrapePaths = list
        toast('添加成功')
        refresh()
        return true
      } else {
        toast('添加失败')
        return false
      }
    } catch (err) {
      console.error(err)
      const msg = getErrorMessage(err)
      const toastMsg = msg === 'timeout' ? '网络开小差了，请重试' : (msg || '删除失败')
      toast(toastMsg)
      logError('cinema', {
        traceId,
        category: 'scrape_path',
        action: 'add_scrape_path_fail',
        desc: '添加刮削目录出现异常',
        params: { paths },
        error: err,
        errorText: msg,
      })
      return false
    }
  }

  watch(
    // deviceId变化，如切换设备
    () => store.state.curDeviceRunner.params?.target || '',
    (newVal, oldVal) => {
      logs('设备Id变化:', newVal, oldVal)
      if (newVal && newVal !== oldVal) {
        init()
      }
    },
  )

  onBeforeMount(() => {
    if (isIniting.value) {
      init()
    }
  })

  return {
    isIniting,
    isLoading,
    data: state,
    error,
    refresh,
    toggleScrapeAll,
    deletePath,
    addPath,
  }
})
